import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/firebase'; // Adjust path to your firebase config

export default function PublicDocumentViewer({ docType }) {
  const { storeId, orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [storeInfo, setStoreInfo] = useState({ address: "", phone: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // 1. Fetch Store Information
        const storeSnap = await getDoc(doc(db, "stores", storeId));
        if (storeSnap.exists()) {
          setStoreInfo({
            address: storeSnap.data().address || "CHANDRAKANTI COMPLEX...",
            phone: storeSnap.data().phone || "06123590450"
          });
        }

        // 2. QUERY the sales collection where invoiceNo matches the URL param
        const salesRef = collection(db, "stores", storeId, "sales");
        const q = query(salesRef, where("invoiceNo", "==", orderId));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          // Take the first matching document
          setOrder({ id: querySnapshot.docs[0].id, ...querySnapshot.docs[0].data() });
        } else {
          setError("Invoice not found.");
        }
      } catch (err) {
        console.error(err);
        setError("Failed to retrieve the document.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [storeId, orderId]);

  if (loading) return <div className="p-10 text-center font-bold text-slate-500">Retrieving Secure Document...</div>;
  if (error || !order) return <div className="p-10 text-center font-bold text-rose-500">{error}</div>;

  // --- HTML GENERATORS ---
  const generateOrderFormHtml = () => {
    const dateObj = new Date(order.timestamp);
    const orderDateStr = dateObj.toLocaleDateString('en-GB');
    const deliveryDateRaw = order.checkout?.deliveryDate || dateObj.toISOString().split("T")[0];
    const deliveryDateStr = deliveryDateRaw.split("-").reverse().join("-");

    const itemsHtml = (order.items || []).map((item, idx) => {
      // 1. Safely extract and format strings (add spaces for separation)
      const pType = item.productType ? item.productType.toUpperCase() + " " : "";
      const pCode = item.productCode ? item.productCode.toUpperCase() + " " : "";
      const pName = item.productName ? item.productName.toUpperCase() : "";

      // 2. Check if the product is a lens
      const pNameLower = item.productName?.toLowerCase() || "";
      const pCodeLower = item.productCode?.toLowerCase() || "";
      const isLens = pNameLower.includes("lens") || pCodeLower.includes("lens") || item.productType === "Glass" || item.productType === "Lens" || item.productType === "Contact Lens";

      // 3. Apply the formatting rule: hide productCode if it's a lens
      const displayTitle = isLens 
        ? `${pType}${pName}`.trim() 
        : `${pType}${pCode}${pName}`.trim();

      return `
        <tr>
        <td class="sl" style="border: 1px solid #000; vertical-align:top; font-weight:normal; padding: 6px 4px;">${idx + 1}</td>
        <td style="border: 1px solid #000; font-weight:normal; padding: 6px 8px;">${displayTitle}</td>
        </tr>
      `;
    }).join('');

    let rxHtml = '';
    const rx = order.prescription;
    if (rx && (rx.sphRight || rx.sphLeft || rx.clSphRight || rx.clSphLeft)) {
        rxHtml = `
        <div class="prescriptionWrapper" style="margin-top: 12px;">
            <h3 style="margin-bottom: 6px; font-size: 13px; font-weight: bold; text-decoration: underline; color: #000;">PRESCRIPTION DETAILS</h3>
            <table class="prescription" style="width: 100%; border-collapse: collapse; text-align: center; font-size: 11px;">
            <tr>
            <td style="border:1px solid #000; padding:3px;"></td>
            <td colspan="5" class="eyeTitle" style="border:1px solid #000; padding:4px; font-weight:bold;">✓ RIGHT EYE (OD)</td>
            <td colspan="5" class="eyeTitle" style="border:1px solid #000; padding:4px; font-weight:bold;">✓ LEFT EYE (OS)</td>
            </tr>
            <tr style="background:#f2f2f2;">
            <td style="border:1px solid #000; padding:3px;"></td>
            <td style="border:1px solid #000; padding:3px; font-weight:bold;">SPH</td><td style="border:1px solid #000; padding:3px; font-weight:bold;">CYL</td><td style="border:1px solid #000; padding:3px; font-weight:bold;">AXIS</td><td style="border:1px solid #000; padding:3px; font-weight:bold;">PD</td><td style="border:1px solid #000; padding:3px; font-weight:bold;">VA</td>
            <td style="border:1px solid #000; padding:3px; font-weight:bold;">SPH</td><td style="border:1px solid #000; padding:3px; font-weight:bold;">CYL</td><td style="border:1px solid #000; padding:3px; font-weight:bold;">AXIS</td><td style="border:1px solid #000; padding:3px; font-weight:bold;">PD</td><td style="border:1px solid #000; padding:3px; font-weight:bold;">VA</td>
            </tr>
            <tr>
            <td style="border:1px solid #000; padding:4px 2px; font-weight:normal;">DV</td>
            <td style="border:1px solid #000; padding:4px 2px; font-weight:normal;">${rx.sphRight || '-'}</td><td style="border:1px solid #000; padding:4px 2px; font-weight:normal;">${rx.cylRight || '-'}</td><td style="border:1px solid #000; padding:4px 2px; font-weight:normal;">${rx.axisRight || '-'}</td><td style="border:1px solid #000; padding:4px 2px; font-weight:normal;">${rx.pdRight || '-'}</td><td style="border:1px solid #000; padding:4px 2px; font-weight:normal;">${rx.vaRight || '-'}</td>
            <td style="border:1px solid #000; padding:4px 2px; font-weight:normal;">${rx.sphLeft || '-'}</td><td style="border:1px solid #000; padding:4px 2px; font-weight:normal;">${rx.cylLeft || '-'}</td><td style="border:1px solid #000; padding:4px 2px; font-weight:normal;">${rx.axisLeft || '-'}</td><td style="border:1px solid #000; padding:4px 2px; font-weight:normal;">${rx.pdLeft || '-'}</td><td style="border:1px solid #000; padding:4px 2px; font-weight:normal;">${rx.vaLeft || '-'}</td>
            </tr>
            <tr>
            <td style="border:1px solid #000; padding:4px 2px; font-weight:normal;">NV</td>
            <td style="border:1px solid #000; padding:4px 2px; font-weight:normal;">${rx.nvSphRight || '-'}</td><td style="border:1px solid #000; padding:4px 2px; font-weight:normal;">${rx.nvCylRight || '-'}</td><td style="border:1px solid #000; padding:4px 2px; font-weight:normal;">${rx.nvAxisRight || '-'}</td><td style="border:1px solid #000; padding:4px 2px; font-weight:normal;">-</td><td style="border:1px solid #000; padding:4px 2px; font-weight:normal;">-</td>
            <td style="border:1px solid #000; padding:4px 2px; font-weight:normal;">${rx.nvSphLeft || '-'}</td><td style="border:1px solid #000; padding:4px 2px; font-weight:normal;">${rx.nvCylLeft || '-'}</td><td style="border:1px solid #000; padding:4px 2px; font-weight:normal;">${rx.nvAxisLeft || '-'}</td><td style="border:1px solid #000; padding:4px 2px; font-weight:normal;">-</td><td style="border:1px solid #000; padding:4px 2px; font-weight:normal;">-</td>
            </tr>
            <tr class="addRow">
            <td style="border:1px solid #000; padding:4px 2px; font-weight:normal;">ADD</td>
            <td colspan="5" style="border:1px solid #000; padding:4px; text-align:left; font-weight:bold; color:#b40000;">${rx.addRight || '-'}</td>
            <td colspan="5" style="border:1px solid #000; padding:4px; text-align:left; font-weight:bold; color:#b40000;">${rx.addLeft || '-'}</td>
            </tr>
            <tr>
            <td colspan="11" class="notes" style="border:1px solid #000; padding:6px 8px; text-align:left; font-weight:normal;">
            <strong style="font-weight:bold; text-decoration:underline; margin-right: 5px;">Prescription Notes:</strong> 
            ${rx.prescriptionNotes || 'As directed by Optometrist.'}
            </td>
            </tr>
            </table>
        </div>`;
    }

    return `
    <!DOCTYPE html>
    <html><head><style>
    *{ margin:0; padding:0; box-sizing:border-box; font-family:Arial, Helvetica, sans-serif; color: #000; } 
    html, body { width: 100%; height: 148.5mm; margin: 0; padding: 0; background:#fff; font-weight: normal; font-size: 11px; line-height: 1.3; } 
    .page{ width: 100%; height: 148.5mm; margin:auto; background:white; padding: 8mm 12mm; overflow: hidden; box-sizing: border-box; } 
    .header{ display:flex; justify-content:space-between; align-items:center; border-bottom:2px solid #000; padding-bottom: 10px; margin-bottom: 8px; } 
    .logo{ flex: 0 0 85px; display:flex; justify-content:center; align-items:center; margin-right: 15px; } 
    .logo img{ width: 85px; height: 85px; object-fit: contain; } 
    .company{ flex:1; text-align:left; } 
    .company h1{ font-size:16px; font-weight:bold; letter-spacing:.5px; margin-bottom:4px; } 
    .company p{ font-size:11px; line-height:1.3; font-weight: normal; margin-bottom: 2px;} 
    .company .small{ margin-top:4px; font-size:10.5px; font-weight: normal; } 
    .orderTitle{ text-align:center; margin: 10px 0; } 
    .orderTitle h2{ font-size:15px; font-weight:bold; text-decoration:underline; } 
    .customerSection{ display:flex; justify-content:space-between; margin-bottom: 12px; padding: 0 5px; } 
    .customerLeft{ width:55%; font-size:12px; font-weight:normal; } 
    .customerLeft strong{ font-size:13px; font-weight:bold; } 
    .customerRight{ width:40%; font-size:11px; line-height:1.5; font-weight:normal; text-align: right;} 
    .mainTable{ width:100%; border-collapse:collapse; margin-bottom: 10px; } 
    .mainTable th{ border:1px solid #000; padding: 6px 8px; text-align:left; font-size:12px; font-weight: bold; background:#f2f2f2; } 
    .sl{ width:40px; text-align:center; } 
    @page{ margin: 0; } 
    @media print{ 
        html, body { width: 100%; height: 148.5mm; }
        .page{ margin:0; padding: 8mm 10mm; width: 100%; height: 148.5mm; box-shadow:none; overflow:hidden; page-break-after: avoid; } 
    }
    </style></head><body>
    <div class="page">
        <div class="header">
            <div class="logo"><img src="${window.location.origin}/logo192.png"></div>
            <div class="company">
                <h1>SHRUTI EYE CARE & OPTICALS</h1>
                <p>${storeInfo.address}</p>
                <p class="small">Mobile No : 9470835831, 9939605949 | Landline No : ${storeInfo.phone}<br>Email ID : shrutieyecare16022021@gmail.com | GST Number : 10CYXPK1491E2ZC</p>
            </div>
        </div>
        <div class="orderTitle"><h2>ORDER FORM</h2></div>
        <div class="customerSection">
            <div class="customerLeft"><strong>Customer Name:</strong> &nbsp; ${order.customer?.name?.toUpperCase() || 'WALK-IN'}</div>
            <div class="customerRight">
                <strong>Order No:</strong> ${order.invoiceNo}<br>
                <strong>Order Date:</strong> ${orderDateStr}<br>
                <strong>Delivery:</strong> ${deliveryDateStr}
            </div>
        </div>
        <table class="mainTable">
            <tr><th class="sl">SL</th><th>PRODUCT DETAILS</th></tr>
            ${itemsHtml}
        </table>
        ${rxHtml}
    </div>
    <script>window.onload = () => window.print();</script>
    </body></html>`;
  };
  const generateReceiptHtml = () => {
    const isAdvance = docType === 'advance-receipt';
    const titleText = isAdvance ? "ADVANCE RECEIPT" : "FINAL INVOICE";
    
    const pad = (n) => n < 10 ? '0' + n : n;
    const dateObj = new Date(order.timestamp);
    const orderDateStr = `${pad(dateObj.getDate())}-${pad(dateObj.getMonth() + 1)}-${dateObj.getFullYear()}`;
    const paymentDateStr = `${orderDateStr} ${pad(dateObj.getHours())}:${pad(dateObj.getMinutes())}`;
    const deliveryDateRaw = order.checkout?.deliveryDate || dateObj.toISOString().split("T")[0];
    const deliveryDateStr = deliveryDateRaw.split("-").reverse().join("-");
    
    const grossTotal = order.financials?.subtotal || 0;
    let actualItemDiscount = 0; let mrpSavings = 0;
    (order.items || []).forEach(i => {
      actualItemDiscount += Number(i.discount || 0);
      const rPrice = Number(i.retailPrice) || 0;
      const mPrice = Number(i.mrp) || rPrice;
      mrpSavings += (mPrice - rPrice) * (Number(i.quantity) || 1);
    });

    const offerDiscountVal = Number(order.checkout?.overallDiscountValue || 0); 
    let computedOfferDisc = order.checkout?.overallDiscountType === 'percent' 
      ? (grossTotal - actualItemDiscount) * (offerDiscountVal / 100) : offerDiscountVal;
    if (computedOfferDisc > (grossTotal - actualItemDiscount)) computedOfferDisc = (grossTotal - actualItemDiscount);

    const netAmount = order.financials?.netPayable || 0;
    const roundOffVal = order.checkout?.roundOff || 0;
    const totalSavedAmount = mrpSavings + actualItemDiscount + computedOfferDisc;
    
    let cashAmt = 0, upiAmt = 0, cardAmt = 0;
    let cashDetails = "-", upiDetails = "-", cardDetails = "-";
    
    if (isAdvance && order.checkout?.paymentHistory?.length > 0) {
        const firstPayment = order.checkout.paymentHistory[0];
        const mode = (firstPayment.mode || "").toUpperCase();
        if (mode.includes("UPI") || mode.includes("GPAY") || mode.includes("PHONEPE")) { upiAmt = firstPayment.amount; upiDetails = firstPayment.details || "-"; }
        else if (mode.includes("CARD")) { cardAmt = firstPayment.amount; cardDetails = firstPayment.details || "-"; }
        else { cashAmt = firstPayment.amount; cashDetails = firstPayment.details || "-"; }
    } else {
        (order.checkout?.paymentHistory || []).forEach(p => {
            const mode = (p.mode || "").toUpperCase();
            if (mode.includes("UPI") || mode.includes("GPAY") || mode.includes("PHONEPE")) { upiAmt += p.amount; upiDetails = p.details || "-"; }
            else if (mode.includes("CARD")) { cardAmt += p.amount; cardDetails = p.details || "-"; }
            else { cashAmt += p.amount; cashDetails = p.details || "-"; }
        });
    }

    const initialAdvance = order.checkout?.advancePaid ?? 0;
    const pendingBalance = isAdvance ? (netAmount - initialAdvance) : (order.financials?.balanceDue || 0);
    
    // UPDATED RX CHECK
    const hasPrescription = !!order.prescription && Object.keys(order.prescription).length > 0;

    return `
    <!DOCTYPE html>
    <html><head><style>
      * { box-sizing: border-box; font-family: 'Segoe UI', Roboto, Arial, sans-serif; font-weight: normal; color: #000; }
      @page { size: A4; margin: 10mm; } 
      body { margin: 0; background: #fff; font-size: 14px; padding: 20px; }
      .info-section { display: flex; justify-content: space-between; gap: 20px; margin-bottom: 5px; text-align: left; }
      
      /* Updated Header styling for 2 columns */
      .header { display: flex; align-items: center; justify-content: flex-start; border-bottom: 2px solid #16205f; padding-bottom: 15px; margin-bottom: 15px; }
      .logo { flex: 0 0 100px; margin-right: 20px; } 
      .logo img { width: 100px; height: 100px; object-fit: contain; }
      .header-content { flex: 1; text-align: left; }
      
      .clinic-name { font-size: 26px; font-weight: bold; color: #16205f; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 4px; }
      .store-address { font-size: 13px; color: #000; font-weight: normal; margin-bottom: 4px; }
      .tagline { color: #000; font-weight: normal; font-size: 13px; margin-bottom: 4px; }
      .info-box { flex: 1; background: #f8fafc; border: 1px solid #000; border-radius: 8px; padding: 15px; text-align: left;margin-bottom: 10px; }
      .info-box h3 { margin: 0 0 12px 0; font-size: 15px; font-weight: bold; text-transform: uppercase; color: #000; border-bottom: 1px solid #000; padding-bottom: 16px; text-align: left; }
      .info-row { display: flex; justify-content: space-between; margin-bottom: 4px; font-size: 14px; text-align: left; }
      .info-row strong { font-weight: bold; }
      table.items { width: 100%; border-collapse: collapse; margin-bottom: 5px; }
      table.items th { background: #f1f5f9; color: #000; font-weight: bold; text-transform: uppercase; font-size: 13px; padding: 10px; text-align: left; border: 1px solid #000; }
      table.items td { padding: 10px; border: 1px solid #000; font-size: 14px; font-weight: normal; color: #000; }
      .text-center { text-align: center; } .text-right { text-align: right; }
      .financial-section { display: flex; gap: 20px; margin-bottom: 5px; align-items: flex-start; margin-top: 10px; }
      .payment-summary { flex: 1; background: #f8fafc; border: 1px solid #000; border-radius: 8px; padding: 15px;margin-bottom:10px; }
      .payment-summary h3 { margin: 0 0 12px 0; font-size: 15px; font-weight: bold; color: #000; border-bottom: 1px solid #000; padding-bottom: 6px; text-transform: uppercase; }
      .totals-summary { width: 340px; border: 1px solid #000; border-radius: 8px; padding: 5px; margin-bottom:10px}
      .summary-row { display: flex; justify-content: space-between; margin-bottom: 5px; font-size: 14px; font-weight: normal; }
      .summary-row.bold { font-weight: bold; color: #000; }
      .summary-row.net-total { font-size: 17px; font-weight: bold; color: #000; border-top: 1px dashed #000; padding-top: 12px; margin-top: 4px; }
      .rx-section { border: 1px solid #000; border-radius: 8px; padding: 5px; margin-bottom: 5px; text-align: left; }
      .rx-section h4 { margin: 0 0 12px 0; font-size: 15px; font-weight: bold; text-transform: uppercase; letter-spacing: 1px; }
      table.rx-table { width: 100%; border-collapse: collapse; font-size: 13px; text-align: center; }
      table.rx-table th, table.rx-table td { border: 1px solid #000; padding: 8px; text-align: center; font-weight: normal; }
      table.rx-table th { background: #f8fafc; color: #000; font-weight: bold; }
      .footer { display: flex; justify-content: space-between; align-items: flex-end; margin-top: 12px; font-size: 13px; font-weight: normal; color: #000; line-height: 1.2; }
      .sign-box { text-align: center; font-weight: bold; color: #000; border-top: 1px solid #000; padding-top: 18px; width: 220px; margin-top:17px; }
    </style></head><body>
      <div class="container">
       <div class="header">
        <div class="logo"><img src="${window.location.origin}/logo192.png" alt="Logo" /></div>
        <div class="header-content"><div class="clinic-name">Shruti Eye Care & Opticals</div>
          <div class="store-address">${storeInfo.address}</br>Mobile No: 9470835831, 9939605949 | Landline: ${storeInfo.phone}</div>
          <div class="tagline">Complete Eye Care • Computerized Eye Testing • Contact Lens • Premium Frames</div></div></div>

        <div class="info-section">
          <div class="info-box">
            <h3>Customer Details</h3>
            <div class="info-row"><span>Name:</span> <strong>${order.customer?.name?.toUpperCase() || 'WALK-IN'}</strong></div>
            <div class="info-row"><span>Mobile:</span> <strong>${order.customer?.phone || 'N/A'}</strong></div>
            <div class="info-row"><span>Address:</span> <strong>${order.customer?.address || 'India'}</strong></div>
          </div>
          <div class="info-box">
            <h3>${titleText}</h3>
            <div class="info-row"><span>Invoice No:</span> <strong>${order.invoiceNo}</strong></div>
            <div class="info-row"><span>Order Date:</span> <strong>${orderDateStr}</strong></div>
            <div class="info-row"><span>Delivery Date:</span> <strong>${deliveryDateStr}</strong></div>
          </div>
        </div>

        <table class="items">
          <thead><tr><th style="width: 8%;" class="text-center">S.No</th><th>Product Description</th><th class="text-center" style="width: 12%;">Qty</th><th class="text-right" style="width: 20%;">Total Price</th></tr></thead>
          <tbody>
            ${(order.items || []).map((i, index) => {
              // 1. Safely extract and format strings (add spaces for separation)
              const pType = i.productType ? i.productType.toUpperCase() + " " : "";
              const pCode = i.productCode ? i.productCode.toUpperCase() + " " : "";
              const pName = i.productName ? i.productName.toUpperCase() : "";

              // 2. Check if the product is a lens
              const pNameLower = i.productName?.toLowerCase() || "";
              const pCodeLower = i.productCode?.toLowerCase() || "";
              const isLens = pNameLower.includes("lens") || pCodeLower.includes("lens") || i.productType === "Glass" || i.productType === "Lens" || i.productType === "Contact Lens";

              // 3. Apply the formatting rule: hide productCode if it's a lens
              const displayTitle = isLens 
                ? `${pType}${pName}`.trim() 
                : `${pType}${pCode}${pName}`.trim();

              return `
              <tr>
                <td class="text-center" style="font-weight:normal;">${index + 1}</td>
                <td><strong style="font-weight:bold;">${displayTitle}</strong></td>
                <td class="text-center" style="font-weight:normal;">${i.quantity}</td>
                <td class="text-right" style="font-weight:normal;">Rs ${(i.retailPrice * i.quantity - (i.discount || 0)).toFixed(2)} <br><span style="font-size:10px;">-Rs ${Number(i.discount || 0).toFixed(2)} (${i.discountValue || 0})</span></td>
              </tr>
              `;
            }).join('')}
          </tbody>
        </table>

        <!-- UPDATED GST BREAKDOWN -->
        ${!isAdvance && order.items && order.items.some(i => Number(i.gstPercent) > 0) ? `
          <div style="margin-top: 10px; margin-bottom: 5px;">
            <h3 style="margin: 0 0 5px 0; font-size: 13px; font-weight: bold; text-transform: uppercase; color: #000; border-bottom: 1px solid #000; padding-bottom: 4px;">GST Breakup Details</h3>
            <table class="items" style="width: 100%; border-collapse: collapse; font-size: 12px;">
              <thead>
                <tr>
                  <th class="text-left">HSN CODE</th>
                  <th class="text-center">GST %</th>
                  <th class="text-right">TAXABLE VALUE</th>
                  <th class="text-right">CGST</th>
                  <th class="text-right">SGST</th>
                  <th class="text-right">TOTAL GST</th>
                </tr>
              </thead>
              <tbody>
                ${order.items.filter(i => Number(i.gstPercent) > 0).map(i => {
                  const gstPct = Number(i.gstPercent) || 0;
                  const netPrice = (Number(i.retailPrice) * Number(i.quantity)) - Number(i.discount || 0);
                  const taxableValue = netPrice / (1 + (gstPct / 100));
                  const totalGst = netPrice - taxableValue;
                  const halfGst = totalGst / 2;
                  
                  return `
                    <tr>
                      <td style="font-weight:normal;">${i.hsnCode || 'N/A'}</td>
                      <td class="text-center" style="font-weight:normal;">${gstPct}%</td>
                      <td class="text-right" style="font-weight:normal;">Rs ${taxableValue.toFixed(2)}</td>
                      <td class="text-right" style="font-weight:normal;">Rs ${halfGst.toFixed(2)} <span style="font-size:10px;">(${gstPct/2}%)</span></td>
                      <td class="text-right" style="font-weight:normal;">Rs ${halfGst.toFixed(2)} <span style="font-size:10px;">(${gstPct/2}%)</span></td>
                      <td class="text-right" style="font-weight:normal;">Rs ${totalGst.toFixed(2)}</td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>
          </div>
        ` : ''}

        <!-- UPDATED PRESCRIPTION RENDER RULE -->
        ${(hasPrescription && isAdvance) ? `
          <table class="rx-table" style="margin-top: 10px;">
            <tr><th></th><th colspan="5">✓ RIGHT EYE (OD)</th><th colspan="5">✓ LEFT EYE (OS)</th></tr>
            <tr><th></th><th>SPH</th><th>CYL</th><th>AXIS</th><th>PD</th><th>VA</th><th>SPH</th><th>CYL</th><th>AXIS</th><th>PD</th><th>VA</th></tr>
            <tr><td><strong style="font-weight:bold;">DV</strong></td><td>${order.prescription.sphRight || '-'}</td><td>${order.prescription.cylRight || '-'}</td><td>${order.prescription.axisRight || '-'}</td><td>${order.prescription.pdRight || '-'}</td><td>${order.prescription.vaRight || '-'}</td><td>${order.prescription.sphLeft || '-'}</td><td>${order.prescription.cylLeft || '-'}</td><td>${order.prescription.axisLeft || '-'}</td><td>${order.prescription.pdLeft || '-'}</td><td>${order.prescription.vaLeft || '-'}</td></tr>
            <tr><td><strong style="font-weight:bold;">NV</strong></td><td>${order.prescription.nvSphRight || '-'}</td><td>${order.prescription.nvCylRight || '-'}</td><td>${order.prescription.nvAxisRight || '-'}</td><td>-</td><td>-</td><td>${order.prescription.nvSphLeft || '-'}</td><td>${order.prescription.nvCylLeft || '-'}</td><td>${order.prescription.nvAxisLeft || '-'}</td><td>-</td><td>-</td></tr>
            <tr><td><strong style="font-weight:bold;">ADD</strong></td><td colspan="5" style="color:#b40000; font-weight:bold; font-size: 15px; text-align:left;">${order.prescription.addRight || '-'}</td><td colspan="5" style="color:#b40000; font-weight:bold; font-size: 15px; text-align:left;">${order.prescription.addLeft || '-'}</td></tr>
          </table>
        ` : ''}

        <div class="financial-section">
          <div class="payment-summary">
            <h3>Payment Logs Breakdown</h3>
            ${cashAmt > 0 ? `<div class="summary-row"><span>CASH Amount:</span> <strong style="font-weight:bold;">Rs ${cashAmt.toFixed(2)}</strong></div>${cashDetails !== "-" ? `<div class="summary-row" style="color: #444; font-size: 12px; margin-top: -8px;"><span>Details: ${cashDetails}</span></div>` : ''}` : ''}
            ${upiAmt > 0 ? `<div class="summary-row mt-2"><span>UPI Amount:</span> <strong style="font-weight:bold;">Rs ${upiAmt.toFixed(2)}</strong></div>${upiDetails !== "-" ? `<div class="summary-row" style="color: #444; font-size: 12px; margin-top: -8px;"><span>Details: ${upiDetails}</span></div>` : ''}` : ''}
            ${cardAmt > 0 ? `<div class="summary-row mt-2"><span>CARD Amount:</span> <strong style="font-weight:bold;">Rs ${cardAmt.toFixed(2)}</strong></div>${cardDetails !== "-" ? `<div class="summary-row" style="color: #444; font-size: 12px; margin-top: -8px;"><span>Details: ${cardDetails}</span></div>` : ''}` : ''}
            <div class="summary-row bold" style="margin-top: 15px; padding-top: 15px; border-top: 1px dashed #000;"><span>Payment Date:</span> <span>${paymentDateStr}</span></div>
            <div class="summary-row bold" style="color: ${pendingBalance > 0 ? '#e11d48' : '#059669'}; font-size:15px;"><span>Balance Left To Pay:</span> <span>Rs ${pendingBalance.toFixed(2)}</span></div>
          </div>
          <div class="totals-summary">
            <div class="summary-row"><span>Gross Total:</span> <strong style="font-weight:bold;">Rs ${grossTotal.toFixed(2)}</strong></div>
            <div class="summary-row" style="color: #b40000;"><span>Item Discount:</span> <strong style="font-weight:bold;">- Rs ${actualItemDiscount.toFixed(2)}</strong></div>
            <div class="summary-row"><span>Round Off (+/-):</span> <strong style="font-weight:bold;">${roundOffVal < 0 ? `- Rs ${Math.abs(roundOffVal).toFixed(2)}` : `+ Rs ${Number(roundOffVal).toFixed(2)}`}</strong></div>
            <div class="summary-row net-total"><span>Payable Amount:</span> <span>Rs ${netAmount.toFixed(2)}</span></div>
            ${totalSavedAmount > 0 ? `<div class="summary-row" style="background: #000; color: #fff; padding: 10px 14px; border-radius: 6px; margin-top: 15px; font-weight: bold; font-size: 15px;"><span style="color:#fff;">TOTAL YOU SAVED:</span> <span style="color:#fff;">Rs ${totalSavedAmount.toFixed(2)}</span></div>` : ''}
            <div class="summary-row bold" style="margin-top: 15px; color: #000; font-size:15px;"><span>Advance Amount:</span> <span>Rs ${initialAdvance.toFixed(2)}</span></div>
          </div>
        </div>

        <div class="footer">
          <div><strong style="font-weight:bold;">Terms & Conditions</strong><br>1. Goods once sold cannot be taken back.<br>2. Guaranteed goods are subject to manufacturer terms.<br>3. Subject to Patna jurisdiction.</div>
          <div class="sign-box">AUTHORISED SIGNATORY</div>
        </div>
      </div>
      <script>window.onload = () => window.print();</script>
    </body></html>`;
  };

  const generatePrescriptionHtml = () => {
    const record = order.prescription;
    const dateObj = new Date(record.timestamp || order.timestamp || Date.now());
    const dateStr = dateObj.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    const nextMonthStr = new Date(dateObj.setMonth(dateObj.getMonth() + 1)).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

    return `
    <!DOCTYPE html>
    <html lang="en"><head><style>
      * { margin:0; padding:0; box-sizing:border-box; font-family: 'Segoe UI', Arial, sans-serif; font-weight: normal; color:#000; }
      @page { size: A4; margin: 10mm; } 
      body { background: #fff; color: #000; -webkit-print-color-adjust: exact; padding: 20px; }
      .a4-page { width: 100%; margin: 0 auto; background: white; }
      
      /* Updated Header styling for 2 columns */
      .header { display: flex; align-items: center; justify-content: flex-start; border-bottom: 2px solid #16205f; padding-bottom: 15px; margin-bottom: 25px; }
      .logo { flex: 0 0 100px; margin-right: 20px; } 
      .logo img { width: 100px; height: 100px; object-fit: contain; }
      .header-content { flex: 1; text-align: left; }
      
      .clinic-name { font-size: 26px; font-weight: bold; color: #16205f; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 5px; }
      .store-address { font-size: 14px; color: #000; font-weight: normal; margin-bottom: 8px; }
      .tagline { color: #000; font-size: 13px; font-weight: normal; margin-bottom: 15px; }
      .title { display: inline-block; font-size: 18px; font-weight: bold; color: #fff; background: #16205f; padding: 8px 25px; border-radius: 20px; text-transform: uppercase; }
      .patient-section { display: flex; justify-content: space-between; margin-bottom: 25px; gap: 25px; }
      .info-box { border: 1px solid #000; border-radius: 8px; padding: 15px; flex: 1; background: #fafafa; text-align: left; }
      .info-box h4 { font-size: 14px; font-weight: bold; color: #000; text-transform: uppercase; margin-bottom: 12px; border-bottom: 1px solid #000; padding-bottom: 5px; text-align: left; }
      .info-row { display: flex; justify-content: space-between; margin-bottom: 8px; font-size: 14px; text-align: left; } .info-row strong { color: #000; font-weight: bold; }
      .section { border: 1px solid #000; border-radius: 8px; margin-bottom: 25px; overflow: hidden; }
      .section-title { background: #f0f4f8; padding: 12px 15px; font-size: 15px; font-weight: bold; border-bottom: 1px solid #000; color: #16205f; text-transform: uppercase; text-align: left; }
      table { width: 100%; border-collapse: collapse; }
      th { background: #fafafa; font-size: 13px; font-weight: bold; padding: 10px; border: 1px solid #000; text-transform: uppercase; color: #000; }
      td { text-align: center; padding: 10px; border: 1px solid #000; font-size: 14px; font-weight: normal; color: #000; }
      .notes { padding: 15px; border: 1px dashed #000; border-radius: 8px; font-size: 14px; font-weight: normal; line-height: 1.6; background: #fffdf9; margin-bottom: 25px; text-align: left; }
      .notes h3 { margin-bottom: 8px; font-size: 15px; font-weight: bold; color: #16205f; text-transform: uppercase; text-decoration: underline; }
      .footer-reminder { display: flex; justify-content: space-between; align-items: center; border: 1px solid #000; border-radius: 8px; padding: 15px 20px; background: #f8f9fa; margin-bottom: 30px; }
      .footer-reminder .text { font-size: 14px; color: #000; text-transform: uppercase; font-weight: bold; } .footer-reminder .date { font-size: 20px; font-weight: bold; color: #b40000; }
      .signature-area { display: flex; justify-content: space-between; margin-top: 50px; padding: 0 20px; }
      .signature { text-align: left; width: 220px; font-size: 14px; color: #000; font-weight: bold; } .signature-line { border-top: 1px solid #000; margin-bottom: 8px; }
      .footer { text-align: center; margin-top: 40px; color: #000; font-size: 12px; font-weight: normal; border-top: 1px solid #000; padding-top: 15px; }
    </style></head><body>
      <div class="a4-page"></br>
      <div class="header">
        <div class="logo"><img src="${window.location.origin}/logo192.png" alt="Logo" /></div>
        <div class="header-content"><div class="clinic-name">Shruti Eye Care & Opticals</div>
          <div class="store-address">  ${storeInfo.address}</br>Mobile No: 9470835831, 9939605949 | Landline: ${storeInfo.phone}</div>
          <div class="tagline">Complete Eye Care • Computerized Eye Testing • Contact Lens • Premium Frames</div>
          <div class="title">Eye Test Prescription</div>
        </div>
      </div>
        <div class="patient-section">
          <div class="info-box"><h4>Patient Profile</h4><div class="info-row"><span>Name:</span> <strong>${record.patient?.name || order.customer?.name || 'Walk-in Patient'}</strong></div><div class="info-row"><span>Mobile:</span> <strong>${record.patient?.mobile || order.customer?.phone || 'N/A'}</strong></div><div class="info-row"><span>Age / Gender:</span> <strong>${record.patient?.age || '-'} / ${record.patient?.gender || '-'}</strong></div></div>
          <div class="info-box"><h4>Clinical Details</h4><div class="info-row"><span>Date:</span> <strong>${dateStr}</strong></div><div class="info-row"><span>Optometrist:</span> <strong>${record.doctor?.name || 'Shruti Eye Care'}</strong></div><div class="info-row"><span>Lens Advised:</span> <strong>${record.lensTypes?.join(', ') || 'As per prescription'}</strong></div></div>
        </div>
        <div class="section"><div class="section-title">Optical Measurement Matrix</div>
          <table><tr><th style="background:#eef2f6;"></th><th colspan="5">✓ RIGHT EYE (OD)</th><th colspan="5">✓ LEFT EYE (OS)</th></tr>
            <tr><th style="background:#eef2f6;"></th><th>SPH</th><th>CYL</th><th>AXIS</th><th>PD</th><th>VA</th><th>SPH</th><th>CYL</th><th>AXIS</th><th>PD</th><th>VA</th></tr>
            <tr><td style="background:#eef2f6; font-weight:bold;">DV</td><td>${record.sphRight || '-'}</td><td>${record.cylRight || '-'}</td><td>${record.axisRight || '-'}</td><td>${record.pdRight || '-'}</td><td>${record.vaRight || '-'}</td><td>${record.sphLeft || '-'}</td><td>${record.cylLeft || '-'}</td><td>${record.axisLeft || '-'}</td><td>${record.pdLeft || '-'}</td><td>${record.vaLeft || '-'}</td></tr>
            <tr><td style="background:#eef2f6; font-weight:bold;">NV</td><td>${record.nvSphRight || '-'}</td><td>${record.nvCylRight || '-'}</td><td>${record.nvAxisRight || '-'}</td><td>-</td><td>-</td><td>${record.nvSphLeft || '-'}</td><td>${record.nvCylLeft || '-'}</td><td>${record.nvAxisLeft || '-'}</td><td>-</td><td>-</td></tr>
            <tr><td style="background:#eef2f6; font-weight:bold;">ADD</td><td colspan="5" style="color:#b40000; font-weight:bold; text-align:left;">${record.addRight || '-'}</td><td colspan="5" style="color:#b40000; font-weight:bold; text-align:left;">${record.addLeft || '-'}</td></tr>
          </table>
        </div>
        ${(record.clSphRight || record.clSphLeft) ? `<div class="section"><div class="section-title">Contact Lens Specification</div><table><tr><th style="background:#eef2f6;">EYE</th><th>SPH</th><th>CYL</th><th>AXIS</th></tr><tr><td style="background:#eef2f6; font-weight:bold;">RIGHT (OD)</td><td>${record.clSphRight || '-'}</td><td>${record.clCylRight || '-'}</td><td>${record.clAxisRight || '-'}</td></tr><tr><td style="background:#eef2f6; font-weight:bold;">LEFT (OS)</td><td>${record.clSphLeft || '-'}</td><td>${record.clCylLeft || '-'}</td><td>${record.clAxisLeft || '-'}</td></tr></table></div>` : ''}
        <div class="notes"><h3>Clinical Remarks</h3><p>${record.prescriptionNotes || "• Use prescribed glasses regularly to prevent eye strain.<br>• Maintain proper screen distance and take breaks.<br>• Please bring this card on your next visit."}</p></div>
        <div class="footer-reminder"><div class="text">Recommended Next Eye Checkup By:</div><div class="date">${nextMonthStr}</div></div>
        <div class="footer">© Shruti Eye Care & Opticals | ${storeInfo.address}</div>
      </div>
      <script>window.onload = () => window.print();</script>
    </body></html>`;
  };

  let finalHtml = '';
  if (docType === 'orderform') finalHtml = generateOrderFormHtml();
  else if (docType === 'prescription') finalHtml = generatePrescriptionHtml();
  else finalHtml = generateReceiptHtml();

  // Rendering inside an iframe sandboxes the custom PDF CSS so it doesn't break your React App styling
  return (
    <iframe
      srcDoc={finalHtml}
      title="Secure PDF Viewer"
      style={{ width: '100%', height: '100vh', border: 'none', background: '#fff' }}
    />
  );
}



